/*
 * 跨模块共享的「叠加宇宙」演示数据：优惠券 + 电子推荐卡的两块奖励券 + 叠加组合。
 *
 * 为什么放在这里而不是某个页面里：叠加关系同时被「优惠券管理 → 叠加规则设置」和
 * 「电子推荐卡管理 → 奖励设置」读写。两边各存一份 state 的话，在推荐卡里配的规则
 * 到规则页就看不见了，因此提两级到 pages 之外由 store 统管。
 *
 * 这里没有引入状态库：演示数据只需要「读同一份、改同一份」，一个极简订阅 store 就够，
 * 避免为一个 mock 场合背上 redux/zustand 的心智负担。
 */

import { useMemo, useSyncExternalStore } from 'react';
import type { RecommendationCard } from '../recommendation-card-management/versions/v1/fixtures';
import { recommendationCards as cardFixtures } from '../recommendation-card-management/versions/v1/fixtures';
import { couponFixtures, couponGroupFixtures, type Coupon, type CouponGroup } from '../coupon-management/versions/v1/fixtures';
import type { UnifiedRule } from '../coupon-management/versions/v1/stackRules';

/** 电子推荐卡的两块奖励：老学员推荐奖励＝老生券，新学员报名奖励＝新生券。 */
export function referrerCouponId(cardId: number): number {
  return cardId * 100 + 1;
}

export function newcomerCouponId(cardId: number): number {
  return cardId * 100 + 2;
}

function unknownCoupon(cardId: number): Coupon {
  return {
    amount: '',
    enabled: true,
    id: cardId,
    issueTime: { type: 'unlimited' },
    kind: 'recommendation',
    name: '',
    source: '电子推荐卡',
    specified: [],
    stackMode: 'none',
    threshold: '',
    type: '',
    useTime: { type: 'relative', days: 30 },
  };
}

function rewardCoupon(cardId: number, id: number, suffix: '老生券' | '新生券', cards: readonly RecommendationCard[]): Coupon {
  const card = cards.find((item) => item.id === cardId);
  /* 卡片不存在时（例如测试只造了券）不让名字变成「undefined－」，退化为可辨认的占位。 */
  return { ...unknownCoupon(id), name: card ? `${card.name}－${suffix}` : `未知推荐卡－${suffix}` };
}

/* 演示用的既有叠加关系：让推荐卡页一进去就能看到「可与 N 张券叠加」，不必先去规则页配。 */
const demoStackPresets: Readonly<Record<number, Pick<Coupon, 'specified' | 'stackMode'>>> = {
  /* 秋季老带新转介绍卡：老生券指定与「语文报名优惠券」「体验课赠2课时」叠加。 */
  301: { specified: [2, 5], stackMode: 'specified' },
  /* 周年庆老带新推荐卡：新生券指定与另一张推荐卡的老生券叠加（演示「卡与卡之间也能叠加」）。 */
  1402: { specified: [101], stackMode: 'specified' },
};

function buildRecommendationCoupons(cards: readonly RecommendationCard[]): readonly Coupon[] {
  return cards.flatMap((card) => [
    { ...rewardCoupon(card.id, referrerCouponId(card.id), '老生券', cards), ...demoStackPresets[referrerCouponId(card.id)] },
    { ...rewardCoupon(card.id, newcomerCouponId(card.id), '新生券', cards), ...demoStackPresets[newcomerCouponId(card.id)] },
  ]);
}

export type RuleSaveInput =
  | { kind: 'single'; mainId: number; specified: readonly number[]; stackMode: 'global' | 'specified' }
  | { couponIds: readonly number[]; groupId?: number; kind: 'group'; name: string };

export type RuleFocusIntent = 'rule' | 'relation';

export interface CouponStoreState {
  cards: readonly RecommendationCard[];
  coupons: readonly Coupon[];
  groups: readonly CouponGroup[];
  /** 跨页跳转带来的诉求：推荐卡里点「去设置 / 查看全部」后，规则页据此自动打开对应弹窗。 */
  pendingFocus: { couponId: number; intent: RuleFocusIntent } | null;
  recommendationCoupons: readonly Coupon[];
}

let state: CouponStoreState = {
  cards: cardFixtures,
  coupons: couponFixtures,
  groups: couponGroupFixtures,
  pendingFocus: null,
  recommendationCoupons: buildRecommendationCoupons(cardFixtures),
};

const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function getSnapshot() {
  return state;
}

/** 不订阅的读取：断言与非 React 场景用。 */
export function getCouponStoreSnapshot(): CouponStoreState {
  return state;
}

function update(patch: (current: CouponStoreState) => CouponStoreState) {
  state = patch(state);
  emit();
}

export function useCouponStore(): CouponStoreState {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** 叠加关系的候选全集：普通优惠券 + 电子推荐卡派生的奖励券。 */
export function useCouponEntities(): readonly Coupon[] {
  const { coupons, recommendationCoupons } = useCouponStore();
  return useMemo(() => [...coupons, ...recommendationCoupons], [coupons, recommendationCoupons]);
}

/** 恢复初始演示数据：测试之间互不影响。 */
export function resetCouponStore() {
  state = {
    cards: cardFixtures,
    coupons: couponFixtures,
    groups: couponGroupFixtures,
    pendingFocus: null,
    recommendationCoupons: buildRecommendationCoupons(cardFixtures),
  };
  emit();
}

/** 新建推荐卡：连同它派生的老生券、新生券一起进叠加宇宙，这样规则页立刻能选到它们。 */
export function addRecommendationCard(card: RecommendationCard) {
  update((current) => ({
    ...current,
    cards: [card, ...current.cards],
    recommendationCoupons: [
      rewardCoupon(card.id, referrerCouponId(card.id), '老生券', [card, ...current.cards]),
      rewardCoupon(card.id, newcomerCouponId(card.id), '新生券', [card, ...current.cards]),
      ...current.recommendationCoupons,
    ],
  }));
}

/** 改卡片自身字段（启用/停用等人工标志）；时间派生状态不受影响。 */
export function updateRecommendationCard(id: number, patch: Partial<RecommendationCard>) {
  update((current) => ({ ...current, cards: current.cards.map((card) => (card.id === id ? { ...card, ...patch } : card)) }));
}

/** 叠加规则可以同时落在普通券和推荐卡奖励券上，因此两处一起写。 */
function writeStack(coupons: readonly Coupon[], id: number, patch: Pick<Coupon, 'specified' | 'stackMode'>): readonly Coupon[] {
  return coupons.map((coupon) => (coupon.id === id ? { ...coupon, ...patch } : coupon));
}

export function saveRule(input: RuleSaveInput) {
  update((current) => {
    if (input.kind === 'single') {
      const patch = { specified: input.stackMode === 'specified' ? [...input.specified] : [], stackMode: input.stackMode };
      return {
        ...current,
        coupons: writeStack(current.coupons, input.mainId, patch),
        recommendationCoupons: writeStack(current.recommendationCoupons, input.mainId, patch),
      };
    }
    if (input.groupId != null) {
      return { ...current, groups: current.groups.map((group) => (group.id === input.groupId ? { ...group, couponIds: [...input.couponIds], name: input.name } : group)) };
    }
    /* 新组的 id 只在演示数据里用，避免与 fixtures 的 201–205 撞车。 */
    return { ...current, groups: [{ couponIds: [...input.couponIds], id: Date.now(), name: input.name }, ...current.groups] };
  });
}

export function deleteRule(rule: UnifiedRule) {
  update((current) => {
    if (rule.kind === 'single') {
      const patch = { specified: [], stackMode: 'none' as const };
      return {
        ...current,
        coupons: writeStack(current.coupons, rule.entity.id, patch),
        recommendationCoupons: writeStack(current.recommendationCoupons, rule.entity.id, patch),
      };
    }
    return { ...current, groups: current.groups.filter((group) => group.id !== rule.group.id) };
  });
}

export function addCoupon(coupon: Coupon) {
  update((current) => ({ ...current, coupons: [coupon, ...current.coupons] }));
}

export function deleteCoupon(id: number) {
  update((current) => ({
    ...current,
    coupons: current.coupons
      .filter((coupon) => coupon.id !== id)
      .map((coupon) => ({ ...coupon, specified: coupon.specified.filter((value) => value !== id) })),
    groups: current.groups
      .map((group) => ({ ...group, couponIds: group.couponIds.filter((value) => value !== id) }))
      .filter((group) => group.couponIds.length >= 2),
  }));
}

export function toggleCouponEnabled(id: number) {
  update((current) => ({ ...current, coupons: current.coupons.map((coupon) => (coupon.id === id ? { ...coupon, enabled: !coupon.enabled } : coupon)) }));
}

export function setPendingFocus(couponId: number, intent: RuleFocusIntent) {
  update((current) => ({ ...current, pendingFocus: { couponId, intent } }));
}

export function consumePendingFocus() {
  update((current) => ({ ...current, pendingFocus: null }));
}
